import { useEffect, useRef, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

// Toolkit imports
import {
  fetchExerciseDetails,
  fetchExerciseVariant,
  fetchLessonAttempt,
  fetchLessonResources,
  getAssessment,
  getQuiz,
  LessonAttemptResponse,
} from "sj-ui-toolkit/src/api";
import { Assessment, Question } from "sj-ui-toolkit/src/api/types/quizTypes";
import { getContentClient, getExperimentClient } from "sj-ui-toolkit/src/client";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { convertLessonDetails } from "../helpers/lessons";

const difficultyMapping: Record<string, string> = {
  "Break/Fix": "Basic",
  Coding_Challenge: "Intermediate",
  Ctf_Challenge: "Advanced",
};

/** Tournament lesson context consumed by the loader (a subset of the tournament content). */
interface TournamentLessonContent {
  assessmentProps?: {
    assessment?: Assessment;
    question?: Question | null;
    questionsData?: Question[];
  };
  experimentPassed?: boolean | null;
  lessonPassed?: boolean | null;
}

/**
 * A single exercise/experiment payload. Its shape varies by source (tournament experiment
 * vs. the Rails exercise API), so the fields the loader touches are kept loose here.
 */
interface LessonExercise {
  id?: number;
  name?: string;
  experiment?: { id: number };
  variant?: unknown;
  [key: string]: unknown;
}

/**
 * Exercise payload passed in for tournaments or exercise-only lessons.
 */
interface ExerciseContentInput {
  experiment?: LessonExercise;
  steps?: number[];
}

/**
 * The assembled lesson-details object surfaced to lesson/exercise/tournament views. It is
 * built up incrementally from several sources, so most fields are optional; T13 can tighten
 * this (and the exercise shapes above) once the upstream lesson/exercise APIs are fully typed.
 */
interface LessonDetails {
  id?: number;
  type?: string;
  title?: string;
  description?: string;
  level?: string;
  difficulty?: string;
  duration?: string | number;
  videoId?: string | null;
  isBreakFix?: boolean;
  hasExercise?: boolean;
  since?: string;
  events?: {
    isConsumed?: boolean;
    watchedVideo?: boolean;
    readTranscript?: boolean;
  };
  assessment?: Assessment;
  question?: Question | null;
  questionsData?: Question[];
  showBeforeSurvey?: boolean | null;
  nextLesson?: unknown;
  experimentPassed?: boolean | null;
  lessonPassed?: boolean | null;
  resources?: unknown[];
  experiment?: LessonExercise;
  variant?: unknown;
  steps?: number[];
  [key: string]: unknown;
}

/**
 * Builds the common lesson details object from Rails data, source-specific overrides,
 * tournament content, and assessment data.
 */
async function buildLessonDetails(
  data: LessonAttemptResponse & { difficulty?: string },
  overrides: Partial<LessonDetails>,
  tournamentContent: TournamentLessonContent | null,
  quizRedesign: boolean
): Promise<LessonDetails> {
  const details: LessonDetails = {
    id: data.lesson.id,
    type: data.type,
    title: data.lesson.name,
    description: data.lesson.description,
    level: data.levelName,
    difficulty: data.lesson.isBreakFixLesson ? difficultyMapping[data.type ?? ""] : data.difficulty,
    duration: data.lesson.duration,
    videoId: data.lesson.videoId,
    isBreakFix: data.lesson.isBreakFixLesson,
    hasExercise: data.lesson.hasExperiment,
    since: data.lesson.since,
    events: {
      isConsumed: data.isConsumed,
      watchedVideo: data.watchedVideo,
      readTranscript: data.readTranscript,
    },
    ...overrides,
  };

  if (tournamentContent?.assessmentProps?.assessment) {
    details.assessment = tournamentContent.assessmentProps.assessment;
    details.question = tournamentContent.assessmentProps.question;
    details.questionsData = tournamentContent.assessmentProps.questionsData;
    details.showBeforeSurvey = false;
  } else if (!quizRedesign) {
    const assessment = await getAssessment(data.lesson.id);
    details.assessment = assessment.assessment;
    details.question = assessment.question;
    details.showBeforeSurvey = assessment.assessment.showBeforeSurvey;
    details.nextLesson = assessment.nextLesson;
  } else {
    const quiz = await getQuiz(data.lesson.lessonKey);
    details.assessment = quiz.assessment;
    details.questionsData = quiz.questionsData;
    details.showBeforeSurvey = quiz.assessment.showBeforeSurvey;
    // `Quiz` doesn't declare nextLesson, but the atlas quiz response carries it (as QuizResponse
    // does); T13 can add it to the toolkit type.
    details.nextLesson = (quiz as { nextLesson?: unknown }).nextLesson;
  }

  details.experimentPassed = tournamentContent?.experimentPassed;
  details.lessonPassed = tournamentContent?.lessonPassed;

  return details;
}

/**
 * useLessonLoader Hook
 *
 * Custom hook to fetch and manage lesson details from multiple sources.
 * Combines data from Rails API and HackEdu content API for break/fix lessons.
 *
 * @param lessonKey - The unique key identifying the lesson
 * @param tournamentContent - In a tournament context, the tournament lesson information
 * @param exerciseContent - Exercise details passed in for tournaments or exercise-only lessons
 */
export function useLessonLoader(
  lessonKey: string | null,
  tournamentContent: TournamentLessonContent | null = null,
  exerciseContent: ExerciseContentInput | null = null
) {
  const contentClient = getContentClient();
  const experimentClient = getExperimentClient();
  const [lessonDetails, setLessonDetails] = useState<LessonDetails | null>(null);
  const exerciseReservationRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { useGetLessonPublic } = useFlags();
  const { quizRedesign } = useFlags();
  const { selectedTenant } = useUserContext();

  // If we have an exercise, make the reservation now and set an interval to re-reserve every 10 minutes
  useEffect(() => {
    if (lessonDetails?.hasExercise && lessonDetails?.variant) {
      const variant = lessonDetails.variant as {
        lessonKey: string;
        lessonVersion: number;
        currentVariant: string;
      };
      experimentClient.prepareTest(
        variant.lessonKey,
        variant.lessonVersion,
        variant.currentVariant
      );

      exerciseReservationRef.current = setInterval(
        () => {
          experimentClient.prepareTest(
            variant.lessonKey,
            variant.lessonVersion,
            variant.currentVariant
          );
        },
        1000 * 10 * 60
      );

      return () => {
        if (exerciseReservationRef.current) clearInterval(exerciseReservationRef.current);
      };
    }
  }, [lessonDetails?.hasExercise, lessonDetails?.variant]);

  // Load lesson details on mount or when lessonKey changes
  useEffect(() => {
    setLessonDetails(null);

    async function doLoad() {
      if (!lessonKey && !exerciseContent) {
        return;
      }

      if (quizRedesign === undefined) {
        return;
      }

      let combinedLessonDetails: LessonDetails = {};
      let svcContentDetails: Partial<LessonDetails> = {};

      if (lessonKey) {
        try {
          const railsDetails = await fetchLessonAttempt(lessonKey);
          if (useGetLessonPublic) {
            const response = await contentClient.getLessonPublic(lessonKey, selectedTenant);
            svcContentDetails = convertLessonDetails(response.data!);
          } else if (railsDetails.lesson.isBreakFixLesson) {
            svcContentDetails = await contentClient.getLessonDetails(lessonKey);
          }
          combinedLessonDetails = await buildLessonDetails(
            railsDetails,
            svcContentDetails,
            tournamentContent,
            quizRedesign
          );
        } catch (error) {
          console.error("Error fetching lesson details:", error);
          return;
        }

        // Use exercise details from exerciseContent (i.e. tournament data) if available
        // otherwise fetch from Rails API
        if (combinedLessonDetails.hasExercise) {
          // exerciseContent (tournament) and fetchExerciseDetails() return differently shaped
          // exercise payloads that the code below reads uniformly; unify loosely here — T13 reconciles.
          const exerciseDetails = (exerciseContent?.experiment ??
            (await fetchExerciseDetails(String(combinedLessonDetails.id)))) as LessonExercise;
          const variantDetails = await fetchExerciseVariant(exerciseDetails.experiment!.id);
          exerciseDetails.variant = variantDetails;
          combinedLessonDetails = { ...combinedLessonDetails, ...exerciseDetails };
        }

        // Load resources
        try {
          const resources = await fetchLessonResources(combinedLessonDetails.id!);
          if (resources?.length > 0) {
            combinedLessonDetails.resources = resources;
          }
        } catch (error) {
          console.error("Error fetching lesson resources:", error);
          return;
        }
      } else if (exerciseContent !== null) {
        // We're in an exercise-only context
        const { experiment, steps } = exerciseContent;
        const title = experiment?.name;
        const variant = await fetchExerciseVariant(experiment!.id!);
        const staticDetails = {
          type: "Exercise",
          level: "Advanced",
          duration: 10,
          hasExercise: true,
        };
        combinedLessonDetails = { title, experiment, variant, steps, ...staticDetails };
      }

      // Update state with combined details
      setLessonDetails(combinedLessonDetails);
    }

    doLoad();
  }, [lessonKey, tournamentContent, exerciseContent, selectedTenant, quizRedesign]);

  return {
    lessonDetails,
    setLessonDetails,
  };
}
